/**
 * Subscription / billing context. Holds the active tenant's real effective feature set
 * (TenantSubscription.EffectiveFeatures — the plan's own features plus any admin override,
 * see SubscriptionsController.GetMySubscription) and exposes feature-flag access.
 * Keep this the ONLY place feature-gating logic lives.
 */
import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react'
import { type Feature } from './plans'

export type BillingStatus = 'Active' | 'PastDue' | 'Cancelled' | 'Expired' | 'none'

export interface SubscriptionState {
  planName: string | null
  features: Feature[]
  status: BillingStatus
  renewsAt?: string | null
  /** Platform admin — bypasses feature gates the same way it bypasses role checks
   * (RequireRolesAttribute backend, routes.tsx's roleMatches/featureMatches). Without this,
   * a gated page would hide its content for an admin whose OWN tenant happens to be on a
   * lower plan, even though the sidebar nav item (already admin-bypassed) still links to it. */
  isAdmin?: boolean
}

const SubscriptionContext = createContext<SubscriptionState | null>(null)

export function SubscriptionProvider({
  children,
  planName = null,
  features = [],
  status = 'none',
  renewsAt,
  isAdmin = false,
}: {
  children: ReactNode
  planName?: string | null
  features?: Feature[]
  status?: BillingStatus
  renewsAt?: string | null
  isAdmin?: boolean
}) {
  const value = useMemo<SubscriptionState>(
    () => ({ planName, features, status, renewsAt, isAdmin }),
    [planName, features, status, renewsAt, isAdmin],
  )
  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>
}

/** Full subscription/billing state. */
export function useBilling(): SubscriptionState {
  const ctx = useContext(SubscriptionContext)
  if (!ctx) throw new Error('useBilling must be used within a <SubscriptionProvider>')
  return ctx
}

/** Boolean gate for a single feature based on the tenant's real effective feature set. */
export function useFeature(feature: Feature): boolean {
  const { features, isAdmin } = useBilling()
  return isAdmin || features.includes(feature)
}

/** Every feature unlocked for the active tenant. */
export function useFeatures(): Feature[] {
  return useBilling().features
}
